import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, BookOpen, Users, Award, TrendingUp, CheckCircle, Star, Play } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const HomePage = () => {
  const [featuredCourses, setFeaturedCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await axios.get(`${API}/courses/featured`);
        setFeaturedCourses(response.data);
      } catch (error) {
        console.error("Error fetching courses:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const stats = [
    { number: "50K+", label: "Students Enrolled", icon: Users },
    { number: "200+", label: "Expert Courses", icon: BookOpen },
    { number: "95%", label: "Success Rate", icon: TrendingUp },
    { number: "4.9", label: "Average Rating", icon: Star },
  ];

  const features = [
    {
      title: "Expert-Led Courses",
      description: "Learn from industry professionals with real-world experience",
      icon: Award,
    },
    {
      title: "Career Counseling",
      description: "Get personalized guidance from certified career counselors",
      icon: Users,
    },
    {
      title: "AI Career Assistant",
      description: "24/7 AI-powered career advice and course recommendations",
      icon: TrendingUp,
    },
    {
      title: "Job Placement Support",
      description: "Connect with top employers and land your dream job",
      icon: CheckCircle,
    },
  ];

  const testimonials = [
    {
      name: "Alexandra Chen",
      role: "Software Engineer at Google",
      image: "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150",
      quote: "WCareers transformed my career. The full-stack bootcamp gave me the skills I needed to land my dream job.",
    },
    {
      name: "Marcus Johnson",
      role: "Product Manager at Meta",
      image: "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150",
      quote: "The career counseling sessions were invaluable. My counselor helped me navigate a successful career transition.",
    },
    {
      name: "Sarah Williams",
      role: "Data Scientist at Netflix",
      image: "https://images.pexels.com/photos/1239291/pexels-photo-1239291.jpeg?auto=compress&cs=tinysrgb&w=150",
      quote: "The data science course exceeded my expectations. The hands-on projects prepared me for real-world challenges.",
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="hero-gradient min-h-[90vh] flex items-center" data-testid="hero-section">
        <div className="container-padding w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-block">
                <span className="overline bg-lime-100 text-lime-700 px-4 py-2 rounded-full">
                  World Class Careers
                </span>
              </div>
              <h1 className="heading-1 opacity-0 animate-fade-in">
                Elevate Your Career to{" "}
                <span className="text-lime-600">New Heights</span>
              </h1>
              <p className="body-text text-lg max-w-xl opacity-0 animate-fade-in animate-delay-100">
                Transform your professional journey with expert-led courses, personalized 
                career counseling, and AI-powered guidance. Join 50,000+ learners building 
                world-class careers.
              </p>
              <div className="flex flex-wrap gap-4 opacity-0 animate-fade-in animate-delay-200">
                <Link to="/courses" data-testid="explore-courses-btn">
                  <Button className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105">
                    Explore Courses
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/counseling" data-testid="book-counseling-btn">
                  <Button variant="outline" className="border-slate-200 hover:bg-slate-50 rounded-full px-8 py-6 text-lg font-medium">
                    Book Counseling
                  </Button>
                </Link>
              </div>
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8 opacity-0 animate-fade-in animate-delay-300">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center md:text-left">
                    <p className="font-heading text-3xl font-semibold text-slate-900">{stat.number}</p>
                    <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative opacity-0 animate-fade-in animate-delay-200">
                <img
                  src="https://images.pexels.com/photos/5940710/pexels-photo-5940710.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Students studying"
                  className="rounded-2xl shadow-2xl"
                  data-testid="hero-image"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-lime-100 rounded-full flex items-center justify-center">
                      <Play className="w-5 h-5 text-lime-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">200+ Courses</p>
                      <p className="text-sm text-slate-500">Start learning today</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white" data-testid="features-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">Why Choose Us</span>
            <h2 className="heading-2 mt-4">Everything You Need for Career Success</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="p-8 border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all duration-300 group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 bg-lime-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-lime-100 transition-colors">
                  <feature.icon className="w-7 h-7 text-lime-600" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Courses */}
      <section className="section-padding bg-slate-50" data-testid="courses-section">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
            <div>
              <span className="overline">Featured Courses</span>
              <h2 className="heading-2 mt-4">Start Your Learning Journey</h2>
            </div>
            <Link to="/courses" data-testid="view-all-courses-link">
              <Button variant="outline" className="rounded-full">
                View All Courses
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-slate-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {featuredCourses.slice(0, 6).map((course) => (
                <Link
                  key={course.id}
                  to={`/courses/${course.id}`}
                  className="bg-white rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300 group"
                  data-testid={`course-card-${course.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={course.image_url}
                      alt={course.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="badge badge-lime">{course.category}</span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-3">
                      <span>{course.duration}</span>
                      <span>•</span>
                      <span>{course.level}</span>
                    </div>
                    <h3 className="font-heading text-xl font-medium text-slate-900 mb-2 group-hover:text-lime-600 transition-colors">
                      {course.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4 line-clamp-2">{course.short_description}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                      <span className="font-semibold text-slate-900">${course.price}</span>
                      <span className="text-sm text-slate-500">{course.enrolled_count?.toLocaleString()} enrolled</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900" data-testid="cta-section">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="overline text-lime-500">Career Counseling</span>
              <h2 className="font-heading text-4xl lg:text-5xl font-light text-white">
                Get Personalized Career Guidance
              </h2>
              <p className="text-slate-400 text-lg">
                Book a session with our expert career counselors. Get personalized advice, 
                resume reviews, interview preparation, and strategic career planning.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/counseling" data-testid="cta-counseling-btn">
                  <Button className="bg-lime-500 text-slate-900 hover:bg-lime-400 rounded-full px-8 py-6 text-lg font-medium">
                    Book a Session
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/ai-assistant" data-testid="cta-ai-btn">
                  <Button variant="outline" className="border-slate-700 text-white hover:bg-slate-800 rounded-full px-8 py-6 text-lg font-medium">
                    Try AI Assistant
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4427552/pexels-photo-4427552.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="Career counseling session"
                className="rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-white" data-testid="testimonials-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">Testimonials</span>
            <h2 className="heading-2 mt-4">What Our Students Say</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="p-8 border border-slate-100 hover:shadow-xl transition-all duration-300"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex items-center gap-1 mb-6">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-lime-500 text-lime-500" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="section-padding bg-slate-50" data-testid="newsletter-section">
        <div className="container-padding">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-2 mb-4">Stay Updated</h2>
            <p className="text-slate-600 mb-8">
              Subscribe to our newsletter for the latest courses, career tips, and industry insights.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 border border-slate-200 rounded-full focus:outline-none focus:ring-2 focus:ring-lime-500"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                className="bg-slate-900 text-white hover:bg-slate-800 rounded-full px-8 py-4"
                data-testid="newsletter-submit-btn"
              >
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
